import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useState } from "react";
import { generateApiKey, hashApiKey } from "../../../../shared/apiKeys";
import { sharedMutators } from "../../../../shared/mutators";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Dropdown } from "../../Dropdown";
import { Icon } from "../../Icon";
import { Modal } from "../../Modal";
import { Text } from "../../Text";
import { GeneratedKeyField } from "../GeneratedKeyField";
import {
  DropdownOpener,
  FieldLabel,
  ModalContent,
  ModalFooter,
} from "../shared";

const TTL_OPTIONS = [
  { label: "1 hour", seconds: 60 * 60 },
  { label: "4 hours", seconds: 4 * 60 * 60 },
  { label: "8 hours", seconds: 8 * 60 * 60 },
  { label: "16 hours", seconds: 16 * 60 * 60 },
] as const;

const DEFAULT_TTL_SECONDS = 8 * 60 * 60;

export function GetTemporaryCredentialModal({
  serviceAccountId,
  onClose,
}: {
  serviceAccountId: string;
  onClose: () => void;
}) {
  const z = useZero();
  const [ttlSeconds, setTtlSeconds] = useState<number>(DEFAULT_TTL_SECONDS);
  const [generating, setGenerating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const generated = revealedKey !== null;

  const generate = async () => {
    if (generating || generated) return;
    setGenerating(true);
    try {
      const rawKey = generateApiKey();
      const hashedKey = await hashApiKey(rawKey);
      z.mutate(
        sharedMutators.createServiceAccountSession({
          id: nanoid(12),
          start: rawKey.slice(0, 12),
          key: hashedKey,
          serviceAccountId,
          ttlSeconds,
        })
      );
      setRevealedKey(rawKey);
    } finally {
      setGenerating(false);
    }
  };

  const selectedLabel =
    TTL_OPTIONS.find((o) => o.seconds === ttlSeconds)?.label ?? "8 hours";

  return (
    <Modal open onClose={onClose} alert>
      <ModalContent>
        <Text variant="text-regular-md">Temporary credentials</Text>

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Expires in</Text>
          </Box>
          <Dropdown
            opener={
              <DropdownOpener>
                <Text variant="text-regular" ellipsis>
                  {selectedLabel}
                </Text>
                <span style={{ opacity: 0.3 }}>
                  <Icon name="chevron-down" size={12} />
                </span>
              </DropdownOpener>
            }
            align="bottom-left"
            sections={[
              {
                items: TTL_OPTIONS.map((o) => ({
                  type: "button" as const,
                  left: <Icon name="clock" size={16} />,
                  label: o.label,
                  right:
                    o.seconds === ttlSeconds ? (
                      <Icon
                        name="check"
                        size={12}
                        color="var(--text-secondary)"
                      />
                    ) : undefined,
                  onClick: () => setTtlSeconds(o.seconds),
                })),
              },
            ]}
          />
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-secondary)">
              The credential acts as this service account and expires on its
              own.
            </Text>
          </Box>
        </FieldLabel>

        <GeneratedKeyField
          label="Credential"
          generateLabel="Generate credential"
          revealedKey={revealedKey}
          generating={generating}
          onGenerate={generate}
        />

        <div style={{ height: 8 }} />
        <ModalFooter>
          <Button size="l" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="l" onClick={onClose} disabled={!generated}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
